import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faChevronDown, faShoppingCart, faShareNodes } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faFacebook, faTiktok, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { restaurantInfo } from '../../data/mock';
import { useCart } from '../../contexts/CartContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverWhiteSection, setIsOverWhiteSection] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null); // Default image state
  const location = useLocation();
  const navigate = useNavigate();
  
  // Cart context
  const { getCartItemsCount, setIsCartOpen } = useCart();

  // Function to navigate to home and scroll to top
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // If already on home page, just scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home page and scroll to top
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Special case: Always use black logo on reservas and gift-cards pages
      if (location.pathname === '/reservas' || location.pathname === '/gift-cards') {
        setIsOverWhiteSection(true);
        return;
      }
      
      // Special case: Always use white logo on nosotros page (dark video background)
      if (location.pathname === '/nosotros') {
        setIsOverWhiteSection(false);
        return;
      }
      
      // Special case for contacto page: white logo in hero, black logo after hero
      if (location.pathname === '/contacto') {
        const heroHeight = 800; // Approximate height of hero section
        setIsOverWhiteSection(window.scrollY > heroHeight);
        return;
      }
      
      // Detect if we're over white sections
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const headerPosition = scrollY + 100; // Header position accounting for top bar
      
      // Define approximate positions of white sections (you may need to adjust these values)
      const whiteSections = [
        { start: 600, end: 1400 },   // Reviews section  
        { start: 1400, end: 2400 },  // Sabores Auténticos section (Menu Highlights)
        { start: 2800, end: 4200 },  // Experiencias que inspiran section (Testimonials + Stats)
        { start: 3200, end: 4500 },  // Partners section (extended)
        { start: 5000, end: 6200 },  // Nuestro Espacio section (Gallery Mosaic)
      ];
      
      const isOnWhite = whiteSections.some(section => 
        headerPosition >= section.start && headerPosition <= section.end
      );
      
      setIsOverWhiteSection(isOnWhite);
    };

    // Initial check for special pages
    if (location.pathname === '/reservas' || location.pathname === '/gift-cards' || location.pathname === '/order-checkout') {
      setIsOverWhiteSection(true);
    } else if (location.pathname === '/nosotros') {
      setIsOverWhiteSection(false);
    } else if (location.pathname === '/contacto') {
      // Initialize based on scroll position
      const heroHeight = 800;
      setIsOverWhiteSection(window.scrollY > heroHeight);
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navigationLinks = [
    { name: 'SABORES', path: '/menu', hasDropdown: true }
  ];

  const menuDropdownItems = [
    { 
      name: 'RESERVAR', 
      path: '/reservas',
      id: 'reservar',
      image: '/assets/images/restaurant/Hero.jpg',
      description: 'Reserva tu mesa en ADASTRA'
    },
    { 
      name: 'EXPERIENCIAS', 
      path: '/experiencias',
      id: 'experiencias',
      image: '/assets/images/eventos/saxofonista-jazz-terraza-tropical.jpg',
      description: 'Vive momentos culinarios extraordinarios'
    },
    { 
      name: 'MENU', 
      path: '/menu',
      id: 'menu',
      image: '/assets/images/menu/Ceviche de Pescado con Vino Blanco.JPG',
      description: 'Descubre nuestra carta completa de sabores auténticos'
    },
    { 
      name: 'GIFT CARDS', 
      path: '/gift-cards',
      id: 'gift-cards',
      image: '/assets/images/menu/mesa con Gin Tonic.jpg',
      description: 'Regala experiencias gastronómicas únicas'
    },
    { 
      name: 'NOSOTROS', 
      path: '/nosotros',
      id: 'nosotros',
      image: '/assets/images/restaurant/lobby de noche.jpg', 
      description: 'Conoce nuestra historia y pasión culinaria'
    },
    { 
      name: 'GALERÍA', 
      path: '/galeria',
      id: 'galeria',
      image: '/assets/images/restaurant/Patio desde mezanini a la izquierda.jpg',
      description: 'Explora nuestro arte gastronómico visual'
    },
    { 
      name: 'CONTACTO', 
      path: '/contacto',
      id: 'contacto',
      image: '/assets/images/restaurant/Fachada.JPG',
      description: 'Encuéntranos y conecta con nosotros'
    },
    { 
      name: 'RESEÑAS', 
      path: '/resenas',
      id: 'resenas',
      image: '/assets/images/clientes/cliente-feliz-coctel-amarillo.jpg',
      description: 'Comparte y lee experiencias auténticas de ADASTRA'
    },
    { 
      name: 'BLOG', 
      path: '/blog',
      id: 'blog',
      image: '/assets/images/eventos/bartenders-maestros-cocteleria-profesional.jpg',
      description: 'Historias, recetas y cultura gastronómica'
    }
  ];

  return (
    <>
      {/* Top Social Media Band - Mobile Optimized */}
      <div className="fixed top-0 left-0 right-0 bg-black text-white z-50" style={{ height: '52px', padding: '10px 0' }}>
        <div className="w-full flex justify-between items-center h-full relative">
          {/* Left side - Contact info - Absolutely positioned */}
          <div className="absolute left-6 text-xs md:text-sm text-gray-300 truncate">
            <span className="hidden sm:inline">Obispo 255 entre Cuba y Aguiar, La Habana | </span>+53 6351 8498
          </div>
          
          {/* Right side - Social icons - Absolutely positioned */}
          <div className="absolute right-6 flex space-x-4 md:space-x-5">
            <a 
              href="https://instagram.com/adastrarestaurant"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-all duration-200 hover:scale-110"
              aria-label="Instagram"
            >
              <FontAwesomeIcon icon={faInstagram} className="text-lg md:text-xl" />
            </a>
            <a 
              href="https://facebook.com/people/Adastra-Restaurante/61568078246941"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-all duration-200 hover:scale-110"
              aria-label="Facebook"
            >
              <FontAwesomeIcon icon={faFacebook} className="text-lg md:text-xl" />
            </a>
            <a 
              href="https://tiktok.com/@adastra_restaurant"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-all duration-200 hover:scale-110"
              aria-label="TikTok"
            >
              <FontAwesomeIcon icon={faTiktok} className="text-lg md:text-xl" />
            </a>
            <a 
              href="https://youtube.com/@adastrarestaurant"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-gray-300 transition-all duration-200 hover:scale-110"
              aria-label="YouTube"
            >
              <FontAwesomeIcon icon={faYoutube} className="text-lg md:text-xl" />
            </a>
            <button
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'ADASTRA Restaurant',
                    text: 'Descubre ADASTRA - Experiencia gastronómica única en La Habana',
                    url: window.location.href
                  }).catch((error) => console.log('Error sharing:', error));
                } else {
                  // Fallback: copiar URL al portapapeles
                  navigator.clipboard.writeText(window.location.href).then(() => {
                    alert('¡Enlace copiado al portapapeles!');
                  }).catch(() => {
                    alert('No se pudo copiar el enlace');
                  });
                }
              }}
              className="text-white hover:text-gray-300 transition-all duration-200 hover:scale-110"
              aria-label="Compartir"
            >
              <FontAwesomeIcon icon={faShareNodes} className="text-lg md:text-xl" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className={`fixed left-0 right-0 z-40 transition-all duration-500 ${
        isScrolled 
          ? 'bg-transparent' 
          : 'bg-transparent'
      }`} style={{ top: '68px', margin: 0, padding: 0 }}>
        <nav className="w-full relative">
          {/* SABORES Button - Absolutely positioned to left edge */}
          <div 
            className="hidden lg:block absolute left-6 top-1/2 transform -translate-y-1/2 z-10"
            onMouseEnter={() => setShowDropdown(true)}
            onMouseLeave={() => setShowDropdown(false)}
          >
                <button
                  className={`flex items-center space-x-1 font-light transition-colors duration-200 ${
                    isOverWhiteSection || location.pathname === '/order-checkout'
                      ? 'text-black hover:text-gray-600' 
                      : 'text-white hover:text-gray-300'
                  }`}
                >
                  <span>SABORES</span>
                  <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 opacity-60" />
                </button>
          </div>

          {/* Desktop Dropdown Menu - Moved outside SABORES div */}
          {showDropdown && (
            <div 
              className={`hidden lg:block fixed left-6 shadow-2xl z-50 rounded-2xl ${
                isOverWhiteSection 
                  ? 'bg-white/95 backdrop-blur-sm' 
                  : 'bg-black/30 backdrop-blur-md'
              }`}
              style={{ top: '52px' }}
              onMouseEnter={() => setShowDropdown(true)}
              onMouseLeave={() => setShowDropdown(false)}
            >
                    <div className="px-6 py-10">
                      <div className="flex gap-20 items-start">
                        
                        {/* Left Column - Simple Text Menu */}
                        <div className="flex flex-col justify-center space-y-2">
                          {menuDropdownItems.map((item) => (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setShowDropdown(false)}
                              onMouseEnter={() => setHoveredItem(item.id)}
                              className="group block transition-all duration-300"
                            >
                              <h4 className={`text-xl font-semibold transition-all duration-300 group-hover:translate-x-2 ${
                                isOverWhiteSection 
                                  ? 'text-black group-hover:text-black' 
                                  : 'text-white group-hover:text-white'
                              }`}>
                                {item.name}
                              </h4>
                            </Link>
                          ))}
                        </div>

                        {/* Right Column - Dynamic Images */}
                        <div className="relative overflow-hidden rounded-2xl shadow-2xl h-[390px] w-[540px] flex-shrink-0">
                          {/* Background image - always visible */}
                          <img
                            src="/assets/images/restaurant/Hero.jpg"
                            alt="ADASTRA Restaurant"
                            className="w-full h-full object-cover"
                          />

                          {/* Specific images for each menu item */}
                          {menuDropdownItems.map((item) => (
                            <div
                              key={item.id}
                              className={`absolute inset-0 transition-opacity duration-1000 ${
                                hoveredItem === item.id ? 'opacity-100' : 'opacity-0'
                              }`}
                            >
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>

                      </div>
                    </div>
            </div>
          )}

          {/* Main Container with Logo */}
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-center py-4">
            
            {/* Center - Logo */}
            <div className="absolute left-1/2 transform -translate-x-1/2 mt-[46px] lg:mt-[43px]">
              <button 
                onClick={handleLogoClick}
                className="transition-all duration-300 cursor-pointer hover:opacity-80 hover:scale-105"
              >
                <img 
                  src={isOverWhiteSection || location.pathname === '/order-checkout' ? '/assets/logos/logo-negro.png' : '/assets/logos/logo-blanco.png'}
                  alt="ADASTRA Restaurant"
                  className="h-24 w-auto object-contain"
                />
              </button>
            </div>
          </div>

          {/* Right Side - Absolutely positioned to right edge */}
          <div className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10">
            <div className="flex items-center space-x-4 lg:space-x-8">
              
              {/* Trabajemos Juntos Button - Desktop */}
              <a 
                href="https://forms.gle/BeTDyd4vPbpikC6n6" 
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex font-light transition-all duration-200 items-center space-x-2 px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 border border-red-600"
              >
                <span>TRABAJEMOS JUNTOS</span>
              </a>

              {/* Cart Button - Desktop */}
              <button
                onClick={() => setIsCartOpen(true)}
                className={`hidden lg:flex font-light transition-colors duration-200 items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100/10 relative ${
                  isOverWhiteSection || location.pathname === '/reservas' || location.pathname === '/gift-cards' || location.pathname === '/order-checkout'
                    ? 'text-black hover:text-gray-600' 
                    : 'text-white hover:text-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faShoppingCart} className="w-5 h-5" />
                {getCartItemsCount() > 0 && (
                  <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {getCartItemsCount()}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`lg:hidden flex items-center justify-center w-8 h-8 transition-colors ${
                  isOverWhiteSection ? 'text-black' : 'text-white'
                }`}
                aria-label="Toggle menu"
              >
                <FontAwesomeIcon 
                  icon={isMenuOpen ? faTimes : faBars} 
                  className="w-5 h-5" 
                />
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div 
              className="fixed left-0 right-0 shadow-xl z-50 bg-white/95 backdrop-blur-sm border-t border-gray-200"
              style={{ top: '68px' }}
            >
              <div className="px-5 py-5 space-y-2.5 relative">
                {/* Close Button - Top Right */}
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-black transition-colors duration-200 rounded-full hover:bg-gray-100"
                  aria-label="Cerrar menú"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                </button>

                {/* Share Button - Bottom Right */}
                <button
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: 'ADASTRA Restaurant',
                        text: 'Descubre ADASTRA - Experiencia gastronómica única en La Habana',
                        url: window.location.href
                      }).catch((error) => console.log('Error sharing:', error));
                    }
                    setIsMenuOpen(false);
                  }}
                  className="absolute bottom-3 right-3 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-black transition-colors duration-200 rounded-full hover:bg-gray-100"
                  aria-label="Compartir"
                >
                  <FontAwesomeIcon icon={faShareNodes} className="w-4 h-4" />
                </button>

                {/* Mobile Menu Items */}
                {menuDropdownItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-base font-semibold text-black hover:text-gray-700 transition-colors duration-300 py-1.5"
                  >
                    {item.name}
                  </Link>
                ))}
                
                {/* Mobile Reservation & Language */}
                <div className="pt-4 mt-4 space-y-2.5 border-t border-gray-300">
                  <Link 
                    to="/reservas" 
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-base font-semibold text-black hover:text-gray-700 transition-colors duration-300"
                  >
                    RESERVAS ↗
                  </Link>
                  <a 
                    href="https://forms.gle/BeTDyd4vPbpikC6n6" 
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMenuOpen(false)}
                    className="block text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors duration-300 py-2 px-3 border border-red-600 rounded-full text-center"
                  >
                    TRABAJEMOS JUNTOS
                  </a>
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      setIsCartOpen(true);
                    }}
                    className="flex items-center space-x-2.5 text-base font-semibold text-black hover:text-gray-700 transition-colors duration-300"
                  >
                    <div className="relative">
                      <FontAwesomeIcon icon={faShoppingCart} className="w-4 h-4" />
                      {getCartItemsCount() > 0 && (
                        <span className="absolute -top-2 -right-2 bg-green-600 text-white text-xs font-bold rounded-full h-4 w-4 flex items-center justify-center">
                          {getCartItemsCount()}
                        </span>
                      )}
                    </div>
                    <span>CARRITO ({getCartItemsCount()})</span>
                  </button>
                  <div className="flex items-center space-x-2 text-white/70">
                    <span className="border-b border-white/70">ES</span>
                    <span>/</span>
                    <span>EN</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;